import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import Blog from './blog/pages/Blog';
import ResearchExplorer from './pages/ResearchExplorer';
import ConceptsExplorer from './pages/ConceptsExplorer';
import Library from './pages/Library';
import BookReader from './pages/BookReader';
import Art from './pages/Art';
import { getBasename } from './utils/baseUrl';
import { posts } from './blog/data/posts';
import portrait from './portrait-playful.jpeg';

const navItems = [
  { href: '#now', label: 'Now' },
  { href: '#research', label: 'Research' },
  { href: '#projects', label: 'Projects' },
  { href: '#writing', label: 'Writing' },
  { href: '#art', label: 'Art' },
  { href: '#teaching', label: 'Teaching' },
  { href: '#contact', label: 'Contact' },
];

const resources = [
  {
    title: 'Eir Data Liberator',
    url: 'https://github.com/BirgerMoell/eir-data-liberator',
    description:
      'A connector-based browser extension for helping patients download and structure their own medical records.',
  },
  {
    title: 'Open Medical Scribe',
    url: 'https://github.com/BirgerMoell/open-medical-scribe',
    description:
      'A modular, privacy-first clinical documentation pipeline with swappable speech and LLM providers.',
  },
  {
    title: 'Health.md / EIR Format',
    url: 'https://github.com/BirgerMoell/health-md-standard',
    description:
      'An open YAML and Markdown standard for health data that is readable by humans and useful to language models.',
  },
  {
    title: 'CBT self-help library',
    url: '/library',
    internal: true,
    description:
      'Structured programs for ADHD, autism, social anxiety, and trauma, written from a clinical psychology perspective.',
  },
  {
    title: 'Open LLM training tools',
    url: 'https://github.com/BirgerMoell/olmix',
    description:
      'Data mixing and long-context utilities for Megatron-LM and OpenEuroLLM-style pretraining work.',
  },
];

const selectedResearch = [
  {
    year: '2026',
    title:
      'High-Accuracy Prediction of Mental Health Scores Using BERT Embeddings Trained Solely on LLM-Generated Synthetic Clinical Descriptions',
    venue: 'Frontiers in Digital Health',
    url: 'https://www.frontiersin.org/journals/digital-health/articles/10.3389/fdgth.2025.1694464',
    note:
      'Privacy-preserving prediction of mental health scores using synthetic clinical descriptions instead of real patient narratives.',
  },
  {
    year: '2025',
    title: 'Medical reasoning in LLMs: an in-depth analysis of DeepSeek R1',
    venue: 'Frontiers in Artificial Intelligence',
    url: 'https://www.frontiersin.org/journals/artificial-intelligence/articles/10.3389/frai.2025.1616145',
    note:
      'A close look at how reasoning models work through medical problems, where they help, and where their explanations become fragile.',
  },
  {
    year: '2025',
    title:
      'Swedish Medical LLM Benchmark (SMLB): Development and Evaluation of a Framework for Assessing Large Language Models in the Swedish Medical Domain',
    venue: 'Frontiers in Artificial Intelligence',
    url: 'https://www.frontiersin.org/journals/artificial-intelligence/articles/10.3389/frai.2025.1557920',
    note:
      'Benchmarking clinical language models in Swedish, with tasks closer to local healthcare reality than generic English exams.',
  },
  {
    year: '2025',
    title:
      'Harm reduction strategies for thoughtful use of large language models in the medical domain',
    venue: 'Journal of Medical Internet Research',
    url: 'https://pubmed.ncbi.nlm.nih.gov/40712151/',
    note:
      'A practical frame for patients and clinicians using AI in medicine before the systems are perfect.',
  },
  {
    year: '2025',
    title: 'Voice Cloning for Dysarthric Speech Synthesis',
    venue: 'arXiv',
    url: 'https://arxiv.org/abs/2503.01266',
    note:
      'Synthetic dysarthric speech as a way to address data scarcity in speech-language pathology.',
  },
  {
    year: '2021',
    title:
      'Multimodal capture of patient behaviour for improved detection of early dementia',
    venue: 'Frontiers in Computer Science',
    url: 'https://www.frontiersin.org/articles/10.3389/fcomp.2021.642633',
    note:
      'Clinical speech, gesture, and behavior capture for early cognitive assessment.',
  },
];

const projects = [
  {
    title: 'OpenEuroLLM tooling',
    url: 'https://github.com/BirgerMoell/open-llm-training-wiki',
    description:
      'Practitioner-facing notes and tooling for data curation, training, post-training, and deployment.',
  },
  {
    title: 'olmix',
    url: 'https://github.com/BirgerMoell/olmix',
    description:
      'A data mixing CLI for Megatron-LM that turns pretraining corpus ratios into something inspectable and reusable.',
  },
  {
    title: 'Open Deep Research',
    url: 'https://github.com/BirgerMoell/open-deep-research',
    description:
      'An OpenAlex-based research agent that exposes the trace: queries, citation expansion, scoring, and report synthesis.',
  },
  {
    title: 'Swedish Chatbot Arena',
    url: 'https://chatbotarena.se',
    description:
      'A public comparison arena for Swedish language models, built around human preference data.',
  },
  {
    title: 'Swedish GPT',
    url: 'https://huggingface.co/birgermoell/swedish-gpt',
    description:
      'One of the early Swedish transformer language models, trained on Swedish Wikipedia and OSCAR.',
  },
  {
    title: 'Eir open skills',
    url: 'https://github.com/Eir-Space/eir-open',
    description:
      'Small, auditable health agent skills for medication lookup, CBT programs, care finding, habits, and nutrition.',
  },
  {
    title: 'Eir provider directory',
    url: 'https://github.com/BirgerMoell/eir-provider-directory',
    description:
      'Swedish healthcare provider discovery with verified self-referral data and service capability enrichment.',
  },
];

const selectedWritingSlugs = [
  'when-the-world-simulator-talks-back',
  'what-students-did-with-ai',
  'ai-psychosis',
  'ai-ready-kunskapsstod',
  'patient-as-integration-point',
  'medical-scribe-pipeline',
  'evaluation-is-infrastructure',
  'data-mixing-product-interface',
  'small-auditable-health-agents',
  'truth-and-order',
  'att-losa-problem-tillsammans',
  'how-do-we-train-the-world-simulator',
];

const writing = selectedWritingSlugs
  .map((slug) => posts.find((post) => post.slug === slug))
  .filter(Boolean)
  .map((post) => ({
    title: post.title,
    url: `/blog/${post.slug}`,
    internal: true,
    description: post.description,
  }));

const libraryWriting = [
  {
    title: 'PhD thesis: Evaluation of Artificial Intelligence in the Medical Domain',
    url: '/PhD_Thesis_Birger_Moell.pdf',
    description:
      'Speech, language, evaluation, harm reduction, and medical AI from my doctoral work at KTH.',
  },
  {
    title: 'Google Scholar profile',
    url: 'https://scholar.google.com/citations?user=4lPZnEAAAAAJ',
    description:
      'The canonical list of papers, preprints, citations, and co-authored work.',
  },
];

const workStreams = [
  {
    label: 'Health data',
    text: 'Liberating records, structuring them, and making them useful without moving trust away from the patient.',
  },
  {
    label: 'Medical AI',
    text: 'Scribes, benchmarks, evaluation, and harm reduction for systems that touch clinical reality.',
  },
  {
    label: 'Language infrastructure',
    text: 'Swedish and European LLM training, data mixing, long-context corpora, and evaluation tools.',
  },
  {
    label: 'Agent skills',
    text: 'Small health tools with explicit provenance, validation, and narrow responsibilities.',
  },
];

const artPreview = [
  '/art/IMG_6233.jpg',
  '/art/IMG_6226.jpg',
  '/art/IMG_6229.jpg',
];

const teaching = [
  'Senior Lecturer in Computational Linguistics at Uppsala University, teaching information retrieval and language technology research and development.',
  'Supervising student work on hallucinations, medical reasoning, math reasoning, model merging, RLHF, clinical speech, and accessible language models.',
  'Five years teaching generative AI and art at Konstfack, where artists tend to ask better questions about authorship and meaning than engineers do.',
  'Workshops on LLM training, speech models, Stable Diffusion, AI fundamentals, and deep learning for technical and non-technical audiences.',
];

const timeline = [
  {
    date: '2026',
    title: 'Senior Research Scientist, AI Sweden',
    detail: 'Training sovereign European LLMs in OpenEuroLLM.',
  },
  {
    date: '2026',
    title: 'Senior Lecturer, Uppsala University',
    detail:
      'Teaching computational linguistics and supervising students working on large language models.',
  },
  {
    date: '2025',
    title: 'Co-founder, Eir Space',
    detail:
      'Building a privacy-first health platform for medical record understanding and evidence-based self-help.',
  },
  {
    date: '2020-2025',
    title: 'PhD in Artificial Intelligence, KTH',
    detail:
      'Evaluation of AI in the medical domain: speech, language, and applications.',
  },
  {
    date: '2015-2026',
    title: 'Licensed psychologist and full-stack developer',
    detail:
      'Clinical CBT, online therapy platforms, trauma work, ADHD interventions, and mental health technology.',
  },
];

const LinkOut = ({ item, className = '' }) => {
  if (item.internal) {
    return (
      <Link to={item.url} className={className}>
        {item.title}
      </Link>
    );
  }

  return (
    <a href={item.url} target="_blank" rel="noopener noreferrer" className={className}>
      {item.title}
    </a>
  );
};

const Home = () => (
  <main>
    <section className="intro-shell">
      <aside className="intro-portrait">
        <img
          src={portrait}
          alt="Birger Moëll"
          className="w-full object-cover"
        />
      </aside>

      <div className="intro-copy">
        <p className="intro-tagline">Building AI for human wellbeing.</p>
      </div>
    </section>

    <section className="stream-shell" aria-label="Core work streams">
        {workStreams.map((stream) => (
          <article key={stream.label}>
            <h2>{stream.label}</h2>
            <p>{stream.text}</p>
          </article>
        ))}
    </section>

    <section id="now" className="section-shell border-y border-stone-200 bg-stone-100/70">
      <div className="section-grid">
        <div>
          <p className="eyebrow">Now</p>
          <h2>Current work</h2>
        </div>
        <div className="content-stack">
          <p>
            My current center of gravity is medical AI and European language
            infrastructure: training sovereign LLMs, evaluating model reasoning
            in healthcare, and building tools for Swedish patients and clinicians.
          </p>
          <p>
            I am also developing Eir Space, supervising student research, teaching
            computational linguistics, writing about AI and healthcare, and keeping
            one foot in clinical psychology so the technology stays accountable
            to real human needs.
          </p>
        </div>
      </div>
    </section>

    <section id="resources" className="section-shell">
      <div className="section-grid">
        <div>
          <p className="eyebrow">Start here</p>
          <h2>Useful resources</h2>
          <p className="section-note">
            The things most likely to be useful if you arrived here from search,
            a paper, a talk, or a recommendation.
          </p>
        </div>
        <div className="item-list">
          {resources.map((item) => (
            <article className="list-item" key={item.title}>
              <h3>
                <LinkOut item={item} />
              </h3>
              <p>{item.description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section id="research" className="section-shell border-y border-stone-200 bg-white">
      <div className="section-grid">
        <div>
          <p className="eyebrow">Research</p>
          <h2>Selected papers and questions</h2>
          <p className="section-note">
            Medical reasoning, Swedish benchmarks, clinical speech, synthetic
            data, and harm reduction in real-world AI use.
          </p>
        </div>
        <div className="research-list">
          {selectedResearch.map((paper) => (
            <article className="paper-row" key={paper.title}>
              <time>{paper.year}</time>
              <div>
                <h3>
                  <a href={paper.url} target="_blank" rel="noopener noreferrer">
                    {paper.title}
                  </a>
                </h3>
                <p className="venue">{paper.venue}</p>
                <p>{paper.note}</p>
              </div>
            </article>
          ))}
          <p className="small-link">
            <a href="https://scholar.google.com/citations?user=4lPZnEAAAAAJ" target="_blank" rel="noopener noreferrer">
              Full publication list on Google Scholar
            </a>
            {' '}·{' '}
            <Link to="/research">Interactive research map</Link>
          </p>
        </div>
      </div>
    </section>

    <section id="projects" className="section-shell">
      <div className="section-grid">
        <div>
          <p className="eyebrow">Projects</p>
          <h2>Things you can try or inspect</h2>
          <p className="section-note">
            I learn by building. Some projects are polished products; others are
            datasets, models, or small research tools.
          </p>
        </div>
        <div className="item-list">
          {projects.map((project) => (
            <article className="list-item" key={project.title}>
              <h3>
                <a href={project.url} target="_blank" rel="noopener noreferrer">
                  {project.title}
                </a>
              </h3>
              <p>{project.description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section id="writing" className="section-shell border-y border-stone-200 bg-stone-100/70">
      <div className="section-grid">
        <div>
          <p className="eyebrow">Writing</p>
          <h2>Essays, thesis, and notes</h2>
          <p className="section-note">
            A curated starting point. The full archive has older notes on
            psychology, code, learning, brands, and models of the mind.
          </p>
        </div>
        <div className="item-list">
          {writing.map((item) => (
            <article className="list-item" key={item.title}>
              <h3>
                <LinkOut item={item} />
              </h3>
              <p>{item.description}</p>
            </article>
          ))}
          {libraryWriting.map((item) => (
            <article className="list-item" key={item.title}>
              <h3>
                <LinkOut item={item} />
              </h3>
              <p>{item.description}</p>
            </article>
          ))}
          <p className="small-link">
            <Link to="/blog">Browse all writing</Link>
          </p>
        </div>
      </div>
    </section>

    <section id="art" className="section-shell">
      <div className="section-grid">
        <div>
          <p className="eyebrow">Art</p>
          <h2>Another way of thinking</h2>
          <p className="section-note">
            Painting is where I let perception, uncertainty, and play do their
            work without turning everything into language.
          </p>
          <p className="small-link">
            <Link to="/art">View selected works</Link>
          </p>
        </div>
        <div className="art-preview-strip" aria-label="Selected art preview">
          {artPreview.map((src) => (
            <Link to="/art" key={src}>
              <img src={src} alt="" loading="lazy" />
            </Link>
          ))}
        </div>
      </div>
    </section>

    <section id="teaching" className="section-shell">
      <div className="section-grid">
        <div>
          <p className="eyebrow">Teaching</p>
          <h2>Making complex ideas usable</h2>
        </div>
        <ul className="plain-list">
          {teaching.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    </section>

    <section id="timeline" className="section-shell border-y border-stone-200 bg-white">
      <div className="section-grid">
        <div>
          <p className="eyebrow">Path</p>
          <h2>Chronology</h2>
        </div>
        <div className="timeline">
          {timeline.map((entry) => (
            <article className="timeline-row" key={`${entry.date}-${entry.title}`}>
              <time>{entry.date}</time>
              <div>
                <h3>{entry.title}</h3>
                <p>{entry.detail}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section id="contact" className="section-shell">
      <div className="section-grid">
        <div>
          <p className="eyebrow">Elsewhere</p>
          <h2>Contact and profiles</h2>
        </div>
        <div className="content-stack">
          <p>
            For university work, use{' '}
            <a href="mailto:birger.moell@lingfil.uu.se">birger.moell@lingfil.uu.se</a>.
            For AI Sweden work, use{' '}
            <a href="mailto:birger.moell@ai.se">birger.moell@ai.se</a>.
            For other things, use{' '}
            <a href="mailto:birger.moell@gmail.com">birger.moell@gmail.com</a>.
          </p>
          <p className="link-line">
            <a href="https://github.com/BirgerMoell" target="_blank" rel="noopener noreferrer">GitHub</a>
            <a href="https://huggingface.co/birgermoell" target="_blank" rel="noopener noreferrer">Hugging Face</a>
            <a href="https://scholar.google.com/citations?user=4lPZnEAAAAAJ" target="_blank" rel="noopener noreferrer">Google Scholar</a>
            <a href="https://www.linkedin.com/in/birgermoell" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            <a href="/cv birger moell 2026-03-16.pdf" target="_blank" rel="noopener noreferrer">CV</a>
          </p>
        </div>
      </div>
    </section>
  </main>
);

const SiteFrame = () => (
  <div className="site-frame">
    <header className="site-header">
      <Link to="/" className="site-title">
        Birger Moëll
      </Link>
      <nav aria-label="Main navigation">
        {navItems.map((item) => (
          <a href={item.href} key={item.href}>
            {item.label}
          </a>
        ))}
        <Link to="/blog">Blog</Link>
        <Link to="/library">Library</Link>
      </nav>
    </header>

    <Routes>
      <Route path="/blog/*" element={<Blog />} />
      <Route path="/research" element={<ResearchExplorer />} />
      <Route path="/concepts" element={<ConceptsExplorer />} />
      <Route path="/library" element={<Library />} />
      <Route path="/library/:bookId" element={<BookReader />} />
      <Route path="/art" element={<Art />} />
      <Route path="/" element={<Home />} />
    </Routes>

    <footer className="site-footer">
      <p>© {new Date().getFullYear()} Birger Moëll</p>
      <p>AI, healthcare, language, psychology, and the occasional useful tool.</p>
    </footer>
  </div>
);

function App() {
  return (
    <Router basename={getBasename()}>
      <SiteFrame />
    </Router>
  );
}

export default App;
